/**
 * BLRRD Selection — rect.js  (Phase 4.B.select.1)
 *
 * Rasterize an axis-aligned rectangle into a Float32 coverage mask.
 *
 * Algorithm — analytic 1-px AA band
 * ─────────────────────────────────
 * For each pixel column index x, compute the fractional coverage along
 * the x-axis as `clamp01(min(x + 1, rect.x + rect.w) - max(x, rect.x))`.
 * Same construction in y. Pixel coverage is the product. This is exact
 * for axis-aligned rectangles with sub-pixel edges (no supersampling
 * needed) — the standard "pixel-as-unit-square coverage" model from
 * any raster graphics text (Foley/van Dam, Heckbert "Coverage masks
 * for antialiasing").
 *
 * Provenance: clean-room. Analytic AA for rectangles is textbook; the
 * formula above is the same one any beginner ray-tracer would derive
 * independently for box coverage.
 */

/**
 * Rasterize `rect` into `mask` (Float32Array(w*h)). The caller decides
 * whether `mask` is pre-zeroed (replace semantics) or carries existing
 * content (in which case the rasterizer should write into a scratch
 * buffer that is then combined via src/selection/combine.js).
 *
 * Out-of-bounds rectangles are clipped silently.
 *
 * @param {Float32Array} mask          destination, length = w*h
 * @param {number} w
 * @param {number} h
 * @param {{x: number, y: number, w: number, h: number}} rect
 *                                      image-pixel coords (allow non-int for AA)
 * @param {boolean} [antiAlias=true]    when false, snap edges to pixels
 * @returns {Float32Array}              the same `mask` reference
 */
export function rasterizeRect(mask, w, h, rect, antiAlias = true) {
    if (!(mask instanceof Float32Array)) {
        throw new Error('rasterizeRect: mask must be Float32Array');
    }
    const W = w | 0, H = h | 0;
    if (mask.length !== W * H) {
        throw new Error(`rasterizeRect: mask length mismatch (${mask.length} vs ${W * H})`);
    }
    if (!rect || !Number.isFinite(rect.x) || !Number.isFinite(rect.y) ||
        !Number.isFinite(rect.w) || !Number.isFinite(rect.h)) {
        /* No-op: caller passed nonsense. Mask stays as-is. */
        return mask;
    }

    let rx = rect.x, ry = rect.y, rw = rect.w, rh = rect.h;
    /* Normalize negative width/height (drag from bottom-right to top-left). */
    if (rw < 0) { rx += rw; rw = -rw; }
    if (rh < 0) { ry += rh; rh = -rh; }
    if (rw === 0 || rh === 0) { mask.fill(0); return mask; }

    const x0 = rx, x1 = rx + rw;
    const y0 = ry, y1 = ry + rh;

    /* Pixel-index bounds we need to touch. Floor/ceil so a sub-pixel
     * edge influences the pixel it overlaps. */
    let xLo, xHi, yLo, yHi;
    if (antiAlias) {
        xLo = Math.max(0, Math.floor(x0));
        xHi = Math.min(W, Math.ceil(x1));
        yLo = Math.max(0, Math.floor(y0));
        yHi = Math.min(H, Math.ceil(y1));
    } else {
        xLo = Math.max(0, Math.round(x0));
        xHi = Math.min(W, Math.round(x1));
        yLo = Math.max(0, Math.round(y0));
        yHi = Math.min(H, Math.round(y1));
    }

    /* Pre-zero only the touched span so the all-1.0 hot pixels stay
     * meaningful when the caller wants a clean replace.
     * Note: combine() handles op-semantics. This rasterizer is the
     * "fresh src mask" build — it MUST start from a zeroed buffer or
     * the caller MUST pass an already-zeroed mask. We zero the full
     * mask defensively so the contract is unambiguous. */
    mask.fill(0);

    if (xHi <= xLo || yHi <= yLo) return mask;   /* fully outside */

    /* Pre-compute per-column coverage so we can multiply with per-row
     * coverage in the inner loop (O(W + H + box) instead of O(W*H)). */
    const colCov = new Float32Array(xHi - xLo);
    for (let x = xLo; x < xHi; x++) {
        let cov;
        if (antiAlias) {
            const a = x > x0 ? x : x0;
            const b = (x + 1) < x1 ? (x + 1) : x1;
            cov = b - a;
            if (cov < 0) cov = 0;
            else if (cov > 1) cov = 1;
        } else {
            cov = 1;
        }
        colCov[x - xLo] = cov;
    }

    for (let y = yLo; y < yHi; y++) {
        let rowCov;
        if (antiAlias) {
            const a = y > y0 ? y : y0;
            const b = (y + 1) < y1 ? (y + 1) : y1;
            rowCov = b - a;
            if (rowCov < 0) rowCov = 0;
            else if (rowCov > 1) rowCov = 1;
        } else {
            rowCov = 1;
        }
        if (rowCov === 0) continue;
        const rowBase = y * W;
        for (let x = xLo; x < xHi; x++) {
            const cov = colCov[x - xLo] * rowCov;
            if (cov > 0) mask[rowBase + x] = cov;
        }
    }
    return mask;
}
